import { RequiredBy } from '@go-shared/types/typeUtils';
import React, { useEffect, useState } from 'react';

type ValidationRule = 'required' | 'lowercased';

export type SupportedForm = RequiredBy<Record<string, unknown>, '_dirty'>;

interface BubbleValidatedChildProps<T extends SupportedForm> {
  trySubmit: () => void;
  validMap: BubbleValidatedMap<T>;
}

type BubbleValidatedMap<T extends SupportedForm> = {
  [key in keyof T]: boolean | undefined;
};

export type BubbleValidatedRules<T extends SupportedForm> = {
  [key in keyof T]?: ValidationRule[];
};

interface BubbleValidatedPropsBase<T extends SupportedForm> {
  form: T;
  rules: BubbleValidatedRules<T>;
  onValidSubmit: () => void;
}

type BubbleValidatedProps<T extends SupportedForm> = BubbleValidatedPropsBase<T> & {
  children: (props: BubbleValidatedChildProps<T>) => React.ReactNode;
};

function validateRule(value: unknown, rules?: ValidationRule[]): [boolean, boolean[]] {
  const checks = (rules || []).map(rule => {
    switch (rule) {
      case 'required':
        // null or undefined are not values
        if (value === null || value === undefined) return false;
        // strings must be > 1 len
        if (typeof value === 'string') return value.length > 0;
        // arrs & objs must have > 1 key
        if (typeof value === 'object') return Object.keys(value).length > 0;
        // everything else is by default fulfilling of this rule
        return true;
      case 'lowercased':
        // strings must be all lowercased
        if (typeof value === 'string') return value === value.toLowerCase();
        // everything else is by default fulfilling of this rule
        return true;
      default:
        const exhaustiveCheck: never = rule;
        return exhaustiveCheck;
    }
  });

  const aggregate = checks.findIndex(c => c === false) === -1;

  return [aggregate, checks];
}

function generateValidatedMap<T extends SupportedForm>(form: T) {
  return Object.keys(form).reduce(
    (acc, x: keyof T) => ({
      ...acc,
      [x]: undefined,
    }),
    {}
  ) as Record<keyof T, boolean | undefined>;
}

export default function BubbleValidated<T extends SupportedForm>(props: BubbleValidatedProps<T>) {
  const { children, form, rules, onValidSubmit } = props;

  const [formCache, setFormCache] = useState(form);
  const [validMap, setValidMap] = useState<BubbleValidatedMap<T>>(generateValidatedMap(form));

  useEffect(() => {
    if (!form._dirty) {
      // brand new form, reset everything
      setFormCache(form);
      setValidMap(generateValidatedMap(form));
      return;
    }

    // not new form, reset only different keys
    const newValidMap = { ...validMap };

    Object.keys(form).forEach((k: keyof T) => {
      if (form[k] !== formCache[k]) {
        newValidMap[k] = undefined;
      }
    });

    setFormCache(form);
    setValidMap(newValidMap);
  }, [form]);

  function trySubmit() {
    const newValidMap = { ...validMap };
    let formValid = true;

    Object.keys(form).forEach((k: keyof T) => {
      const [isKValid] = validateRule(form[k], rules[k]);
      newValidMap[k] = isKValid;
      formValid = formValid && isKValid;
    });

    setValidMap(newValidMap);

    if (formValid) {
      onValidSubmit();
    }
  }

  return <>{children({ trySubmit, validMap })}</>;
}

type BubbleValidatedStatusProps<T extends SupportedForm> = Pick<
  BubbleValidatedChildProps<T>,
  'validMap'
> &
  Pick<BubbleValidatedProps<T>, 'rules'> & { keyToCheck: keyof T };

export function BubbleValidatedStatus<T extends SupportedForm>(
  props: BubbleValidatedStatusProps<T>
) {
  const { rules, validMap, keyToCheck } = props;

  const errors = (rules[keyToCheck] || [])
    .map(r => {
      switch (r) {
        case 'required':
          return 'This field is required.';
        case 'lowercased':
          return 'Text must be all lowercased.';
        default:
          const exhaustiveCheck: never = r;
          return exhaustiveCheck;
      }
    })
    .join(' ');

  if (validMap[keyToCheck] === false) return <div className="text-crimson">{errors}</div>;

  return <></>;
}
